import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { labAPI, workerAPI, settingsLabPrinterAPI } from '../../services/api';
import { fabricSewingTypeAPI } from '../../services/fabricSewingTypeAPI';
import { useNotification } from '../../hooks/useNotification';
import LabBulkAssignmentModal from './LabBulkAssignmentModal';

const LabList = () => {
  const { t, i18n } = useTranslation();
  const { showSuccess, showError, showWarning } = useNotification();
  const [tasks, setTasks] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterWorker, setFilterWorker] = useState('');
  const [filterFabricType, setFilterFabricType] = useState('');
  const [fabricTypes, setFabricTypes] = useState([]);
  const [showTaskDetailsModal, setShowTaskDetailsModal] = useState(null);
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [printerSettings, setPrinterSettings] = useState({});
  const [returnStatus, setReturnStatus] = useState({});

  const statusColors = {
    'Pending': 'bg-gray-100 text-gray-700',
    'Assigned': 'bg-blue-100 text-blue-700',
    'In Progress': 'bg-yellow-100 text-yellow-700',
    'Completed': 'bg-green-100 text-green-700',
    'On Hold': 'bg-orange-100 text-orange-700',
    'Cancelled': 'bg-red-100 text-red-700'
  };

  // Load printer settings once on component mount
  useEffect(() => {
    loadPrinterSettings();
  }, []);

  useEffect(() => {
    loadData();
    loadFabricTypes();
  }, [search, filterStatus, filterWorker]);

  useEffect(() => {
    loadData();
  }, [filterFabricType]);

  const loadPrinterSettings = async () => {
    try {
      const res = await settingsLabPrinterAPI.get();
      const settings = res.data.data || {};
      setPrinterSettings(settings);
    } catch (err) {
      console.error('Error loading printer settings', err);
    }
  };

  const loadFabricTypes = async () => {
    try {
      const res = await fabricSewingTypeAPI.getActive();
      setFabricTypes(res.data.data || res.data || []);
    } catch (err) {
      console.error('Error loading fabric types', err);
      setFabricTypes([]);
    }
  };

  const loadReturnStatus = async (tasksToCheck) => {
    if (!tasksToCheck || tasksToCheck.length === 0) return;

    try {
      // Group tasks by sales order ID to minimize API calls
      const byOrder = {};
      tasksToCheck.forEach(task => {
        if (!byOrder[task.sales_order_id]) {
          byOrder[task.sales_order_id] = [];
        }
        byOrder[task.sales_order_id].push(task);
      });

      const status = {};
      // Fetch return availability for each order
      for (const orderId in byOrder) {
        try {
          const response = await fetch(`/api/returns/available-quantities/${orderId}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });

          if (response.ok) {
            const data = await response.json();
            (data.data || []).forEach(item => {
              status[item.sales_order_item_id] = {
                available: item.available_quantity,
                already_returned: item.already_returned,
                is_fully_returned: item.available_quantity === 0
              };
            });
          }
        } catch (err) {
          console.error(`Error loading return status for order ${orderId}:`, err);
        }
      }
      setReturnStatus(status);
    } catch (error) {
      console.error('Error loading return status:', error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const params = { search };
      if (filterStatus) params.status = filterStatus;
      if (filterWorker) params.worker_id = filterWorker;
      if (filterFabricType) params.fabric_sewing_type_id = filterFabricType;

      const [tasksResponse, workersResponse] = await Promise.all([
        labAPI.getAll(params),
        workerAPI.getAll({})
      ]);

      // labAPI returns tasks array
      const rawTasks = tasksResponse.data.data || tasksResponse.data || [];
      // Client-side filter: ensure we only show tasks that have a fabric sewing type (defensive)
      const filteredTasks = rawTasks.filter(t => {
        const soi = t.sales_order_item || t.salesOrderItem || null;
        const fabricId = soi ? (soi.fabric_sewing_type_id || soi.fabricSewingTypeId || null) : null;
        return !!fabricId;
      });
      setTasks(filteredTasks);
      setWorkers(workersResponse.data.data || workersResponse.data || []);

      // Load return status for all tasks
      await loadReturnStatus(filteredTasks);
    } catch (error) {
      console.error('Error loading data:', error);
      showError(t('messages.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleStartWork = async (id) => {
    try {
      await labAPI.startWork(id);
      loadData();
    } catch (error) {
      showError(error.response?.data?.message || t('messages.error'));
    }
  };

  const handleCompleteWork = async (id) => {
    try {
      await labAPI.completeWork(id);
      loadData();
    } catch (error) {
      showError(error.response?.data?.message || t('messages.error'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('messages.confirmDelete'))) return;
    try {
      await labAPI.delete(id);
      loadData();
    } catch (error) {
      console.error('Error deleting task:', error);
      showError(t('messages.error'));
    }
  };

  const getWorkerName = (workerId) => {
    if (!workerId) return 'Unassigned';
    // Handle both string and number ID comparisons
    const worker = workers.find(w => String(w.id) === String(workerId));
    return worker ? worker.full_name : 'Unassigned';
  };

  const getFabricTypeName = (id) => {
    if (!id) return '-';
    const ft = fabricTypes.find(f => String(f.id) === String(id));
    return ft ? `${ft.name} (${ft.code})` : '-';
  };

  const getMeasurements = (task) => {
    const soi = task.sales_order_item || task.salesOrderItem || null;
    if (!soi) return null;
    
    const measurement = soi.measurement || soi.sales_order?.measurement || null;
    if (!measurement) return null;
    
    const { length, chest, waist, hips } = measurement;
    return { length, chest, waist, hips };
  };

  const handlePrint = async (task) => {
    // Fetch latest printer settings before printing
    let currentSettings = printerSettings;
    try {
      const res = await settingsLabPrinterAPI.get();
      currentSettings = res.data.data || printerSettings;
    } catch (err) {
      console.error('Error fetching latest printer settings', err);
    }

    const defaultIP = currentSettings.host || '';
    const ip = defaultIP || window.prompt('Printer IP');
    if (!ip) return;
    const soi = task.sales_order_item || task.salesOrderItem || null;
    const m = soi && (soi.measurement || {});
    const lab = task.lab_number || '';
    const soId = task.sales_order_id || '';
    const workerName = getWorkerName(task.worker_id);
    const so = task.sales_order || task.salesOrder || null;
    let width = parseInt(currentSettings.width || '384', 10);
    const maxW = 648;
    width = Math.min(Math.max(width, 200), maxW);
    if (width % 8 !== 0) width += (8 - (width % 8));
    const lineH = parseInt(currentSettings.font_size || '24', 10);

    // Build lines array based on print field settings
    const lines = [];
    const measurements = [];

    // Order Info (if enabled)
    if (currentSettings.show_order_info) {
      lines.push('قياسات الطلب');
      lines.push(`رقم المعمل: ${lab}`);
      lines.push(`رقم الطلب: SO-${soId}`);
      lines.push(`العامل: ${workerName}`);
    }

    // Measurements (if enabled)
    if (currentSettings.show_measurements) {
      measurements.push(
        { label: 'الطول', value: m.length ?? '-' },
        { label: 'الصدر', value: m.chest ?? '-' },
        { label: 'الخصر', value: m.waist ?? '-' },
        { label: 'الأرداف', value: m.hips ?? '-' },
        { label: 'الكتف', value: m.shoulder ?? '-' },
        { label: 'الكم', value: m.sleeve ?? '-' },
        { label: 'الرقبة', value: m.neck ?? '-' }
      );
    }

    // Lab Task fields (if enabled)
    if (currentSettings.show_lab_status) {
      lines.push(`الحالة: ${task.status || '-'}`);
    }
    if (currentSettings.show_lab_created_date) {
      lines.push(`تاريخ الإنشاء: ${task.created_at ? new Date(task.created_at).toLocaleDateString('ar-EG') : '-'}`);
    }

    // Sales Order fields (if enabled)
    if (currentSettings.show_sales_customer_name && so) {
      lines.push(`العميل: ${so.customer_name || so.customerName || '-'}`);
    }
    if (currentSettings.show_sales_order_date && so) {
      lines.push(`تاريخ الطلب: ${so.order_date || so.orderDate ? new Date(so.order_date || so.orderDate).toLocaleDateString('ar-EG') : '-'}`);
    }
    if (currentSettings.show_sales_delivery_date && so) {
      lines.push(`تاريخ التسليم: ${so.expected_delivery_date || so.expectedDeliveryDate ? new Date(so.expected_delivery_date || so.expectedDeliveryDate).toLocaleDateString('ar-EG') : '-'}`);
    }
    if (currentSettings.show_sales_quantity && soi) {
      lines.push(`الكمية: ${soi.quantity || soi.piece_count || '-'}`);
    }
    const canvas = document.createElement('canvas');
    let logoW = 0;
    let logoH = 0;
    let imgEl = null;
    // Check if logo should be shown (default to true if not explicitly disabled)
    const showLogo = currentSettings.show_logo !== false;
    let logoUrl = currentSettings.logo_url;

    // Convert storage URL to API endpoint for CORS support
    if (logoUrl && logoUrl.includes('/storage/logos/')) {
      const filename = logoUrl.split('/storage/logos/')[1];
      // Use full backend URL with API endpoint
      logoUrl = `https://moustache-uae.net/api/logo/${filename}`;
    }

    const loadImage = (src, useAnon = true) => new Promise((resolve, reject) => {
      const im = new Image();
      im.crossOrigin = useAnon ? 'anonymous' : 'use-credentials';
      im.onload = () => resolve(im);
      im.onerror = reject;
      im.src = src;
    });

    if (showLogo && logoUrl) {
      try {
        // Try fetching as blob first to avoid CORS/tainted canvas issues
        const res = await fetch(logoUrl, { credentials: 'include' });
        if (res.ok) {
          const blob = await res.blob();
          const localUrl = URL.createObjectURL(blob);
          imgEl = await loadImage(localUrl, true);
        } else {
          throw new Error(`HTTP ${res.status}`);
        }
      } catch (_error1) {
        try {
          // Try with anonymous CORS
          imgEl = await loadImage(logoUrl, true);
        } catch (_error2) {
          try {
            // Try with credentials
            imgEl = await loadImage(logoUrl, false);
          } catch (_error3) {
            // Logo failed to load - continue printing without logo
            console.warn('Logo failed to load. Printing without logo.', { url: logoUrl });
            imgEl = null;
          }
        }
      }

      if (imgEl) {
        // Get user-defined logo dimensions from settings
        const targetW = parseInt(currentSettings.logo_width || '200', 10);
        const targetH = parseInt(currentSettings.logo_height || '100', 10);
        const logoFit = currentSettings.logo_fit || 'contain';

        // Calculate actual dimensions based on fit mode
        if (logoFit === 'cover') {
          // Cover: scale to fill the target dimensions, possibly cropping
          const imgRatio = imgEl.width / imgEl.height;
          const targetRatio = targetW / targetH;
          if (imgRatio > targetRatio) {
            logoW = Math.floor(targetH * imgRatio);
            logoH = targetH;
          } else {
            logoW = targetW;
            logoH = Math.floor(targetW / imgRatio);
          }
        } else {
          // Contain: scale to fit within target dimensions
          const imgRatio = imgEl.width / imgEl.height;
          const targetRatio = targetW / targetH;
          if (imgRatio > targetRatio) {
            logoW = targetW;
            logoH = Math.floor(targetW / imgRatio);
          } else {
            logoW = Math.floor(targetH * imgRatio);
            logoH = targetH;
          }
        }
      }
    }
    const marginTop = 8;
    const marginBottom = 12;
    const padding = Math.floor(lineH / 2);
    const rowH = lineH + 6;
    const height = marginTop + logoH + (imgEl ? padding : 0) + lines.length * rowH + measurements.length * rowH + marginBottom;
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, width, height);
    ctx.fillStyle = '#000000';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.font = `${lineH}px Arial, Tahoma, sans-serif`;
    ctx.direction = 'rtl';
    let y = marginTop;
    if (imgEl && logoW && logoH) {
      const x = Math.floor((width - logoW) / 2);
      ctx.drawImage(imgEl, x, y, logoW, logoH);
      y += logoH + padding;
    }
    const boxMargin = 12;
    const boxX = boxMargin;
    const boxW = width - boxMargin * 2;
    lines.forEach((text) => {
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 1;
      ctx.strokeRect(boxX, y - 3, boxW, rowH);
      ctx.fillText(text, Math.floor(width / 2), y);
      y += rowH;
    });

    // Render measurements as 1 per row (RTL: value on left, label on right)
    if (measurements.length > 0) {
      const colW = boxW / 2;
      measurements.forEach((measurement) => {
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 1;

        // Left column - Value (RTL layout)
        ctx.strokeRect(boxMargin, y - 3, colW, rowH);
        ctx.textAlign = 'center';
        ctx.fillText(measurement.value, boxMargin + colW / 2, y);

        // Right column - Label (RTL layout)
        ctx.strokeRect(boxMargin + colW, y - 3, colW, rowH);
        ctx.fillText(measurement.label, boxMargin + colW + colW / 2, y);

        y += rowH;
      });
    }

    let img;
    try {
      img = ctx.getImageData(0, 0, canvas.width, height);
    } catch (e) {
      // Canvas was tainted by logo - need to handle specially
      // If logo was loaded, we still try to use canvas without getImageData
      // Otherwise, redraw the entire canvas
      if (!imgEl) {
        // No logo was loaded, safe to redraw
        const noLogoHeight = marginTop + lines.length * rowH + measurements.length * rowH + marginBottom;
        canvas.height = noLogoHeight;
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, width, noLogoHeight);
        ctx.fillStyle = '#000000';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        ctx.font = `${lineH}px Arial, Tahoma, sans-serif`;
        ctx.direction = 'rtl';
        let ty = marginTop;
        lines.forEach((text) => {
          ctx.strokeStyle = '#000000';
          ctx.lineWidth = 1;
          ctx.strokeRect(boxX, ty - 3, boxW, rowH);
          ctx.fillText(text, Math.floor(width / 2), ty);
          ty += rowH;
        });
        // Render measurements in fallback (1 per row, RTL: value on left, label on right)
        if (measurements.length > 0) {
          const colW = boxW / 2;
          measurements.forEach((measurement) => {
            ctx.strokeStyle = '#000000';
            ctx.lineWidth = 1;
            ctx.strokeRect(boxMargin, ty - 3, colW, rowH);
            ctx.textAlign = 'center';
            ctx.fillText(measurement.value, boxMargin + colW / 2, ty);
            ctx.strokeRect(boxMargin + colW, ty - 3, colW, rowH);
            ctx.fillText(measurement.label, boxMargin + colW + colW / 2, ty);
            ty += rowH;
          });
        }
        img = ctx.getImageData(0, 0, width, noLogoHeight);
      } else {
        // Logo was loaded but canvas is tainted - use toDataURL instead
        // This is a fallback for when the logo causes CORS issues
        try {
          const dataURL = canvas.toDataURL('image/png');
          const link = document.createElement('a');
          link.href = dataURL;
          link.download = 'lab_task_print.png';
          link.click();
          showSuccess(t('messages.success') + ': Check your downloads for lab_task_print.png');
          return;
        } catch (err2) {
          console.error('Failed to process tainted canvas:', err2);
          showError('Logo loading caused CORS issue. Please use a local image or disable logo.');
          return;
        }
      }
    }
    const actualHeight = canvas.height;
    const wBytes = Math.ceil(width / 8);
    const xL = wBytes & 0xFF;
    const xH = (wBytes >> 8) & 0xFF;
    const yL = actualHeight & 0xFF;
    const yH = (actualHeight >> 8) & 0xFF;
    const header = [0x1D, 0x76, 0x30, 0x00, xL, xH, yL, yH];
    const data = new Uint8Array(header.length + wBytes * actualHeight);
    for (let i = 0; i < header.length; i++) data[i] = header[i];
    let offset = header.length;
    for (let row = 0; row < actualHeight; row++) {
      for (let byte = 0; byte < wBytes; byte++) {
        let b = 0;
        for (let bit = 0; bit < 8; bit++) {
          const x = byte * 8 + bit;
          if (x >= width) continue;
          const idx = (row * width + x) * 4;
          const r = img.data[idx];
          const g = img.data[idx + 1];
          const bl = img.data[idx + 2];
          const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * bl;
          const isBlack = luminance < 160;
          b |= (isBlack ? 1 : 0) << (7 - bit);
        }
        data[offset++] = b;
      }
    }
    const b64 = btoa(String.fromCharCode.apply(null, Array.from(data)));
    try {
      await labAPI.print(task.id, { printer_ip: ip, printer_port: parseInt(currentSettings.port || '9100', 10), data_base64: b64 });
      showSuccess(t('common.success'));
    } catch (err) {
      showError(err.response?.data?.message || t('messages.error'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-4xl font-bold text-gray-900 flex items-center gap-3">
            {t('lab.title')}
            <div className="bg-yellow-100 p-2 rounded-lg">
              <svg className="w-6 h-6 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10.5 1.5H5.75A2.25 2.25 0 003.5 3.75v12.5A2.25 2.25 0 005.75 18.5h8.5a2.25 2.25 0 002.25-2.25V6.5m-11-5v5m5.5-5v5m4-10h-2.5" />
              </svg>
            </div>
          </h2>
          <p className="text-gray-600 mt-2 text-lg">{t('lab.subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowBulkAssignModal(true)}
            className="flex items-center justify-center px-6 py-3 bg-gray-700 hover:bg-gray-800 text-white rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl font-bold"
          >
            <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('lab.bulkAssign')}
          </button>
          <Link
            to="/lab/new"
            className="flex items-center justify-center px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-gray-900 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl font-bold"
          >
            <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('lab.createTask')}
          </Link>
        </div>
      </div>

      {/* Filters & Search Section */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="p-6 border-b-2 border-gray-200 bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search Input */}
            <div className="relative">
              <svg className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder={t('common.search')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all duration-200 font-medium"
              />
            </div>

            {/* Status Filter */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all duration-200 font-medium bg-white"
            >
              <option value="">{t('common.all')} {t('common.status')}</option>
              <option value="Pending">{t('lab.statuses.Pending')}</option>
              <option value="Assigned">{t('lab.statuses.Assigned')}</option>
              <option value="In Progress">{t('lab.statuses.InProgress')}</option>
              <option value="Completed">{t('lab.statuses.Completed')}</option>
              <option value="On Hold">{t('lab.statuses.OnHold')}</option>
              <option value="Cancelled">{t('lab.statuses.Cancelled')}</option>
            </select>

            {/* Worker Filter */}
            <select
              value={filterWorker}
              onChange={(e) => setFilterWorker(e.target.value)}
              className="px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all duration-200 font-medium bg-white"
            >
              <option value="">{t('common.all')} Workers</option>
              {workers.map(worker => (
                <option key={worker.id} value={worker.id}>{worker.full_name}</option>
              ))}
            </select>

            {/* Fabric Type Filter */}
            <select
              value={filterFabricType}
              onChange={(e) => setFilterFabricType(e.target.value)}
              className="px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all duration-200 font-medium bg-white"
            >
              <option value="">All Fabric Types</option>
              {fabricTypes.map(ft => (
                <option key={ft.id} value={ft.id}>{ft.name} ({ft.code})</option>
              ))}
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-gray-900 to-gray-800">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">{t('lab.labNumber')}</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">{t('lab.salesOrder')}</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">{t('salesOrders.expectedDeliveryDate')}</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">{t('lab.fabricType')}</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">{t('measurements.bodyMeasurements')}</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">{t('lab.assignedWorker')}</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">{t('common.status')}</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">{t('lab.createdLabel')}</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">{t('lab.started')}</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {tasks.length === 0 ? (
                <tr>
                  <td colSpan="10" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center text-gray-500">
                      <svg className="h-16 w-16 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="text-lg font-medium">{t('common.noData')}</p>
                    </div>
                  </td>
                </tr>
              ) : (
                tasks.map((task) => (
                  <tr key={task.id} className="hover:bg-yellow-50 transition-colors border-l-4 border-l-transparent hover:border-l-yellow-500">
                    <td className="px-6 py-4 whitespace-nowrap font-semibold text-gray-900">{task.lab_number}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link to={`/sales-orders/${task.sales_order_id}`} className="text-blue-600 hover:text-blue-900 font-medium">
                        SO-{task.sales_order_id}
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {(() => {
                        const so = task.sales_order || task.salesOrder || null;
                        const date = so ? (so.expected_delivery_date || so.expectedDeliveryDate || so.delivery_date || null) : null;
                        return date ? new Date(date).toLocaleDateString() : '-';
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(() => {
                        const soi = task.sales_order_item || task.salesOrderItem || null;
                        const fabricId = soi ? (soi.fabric_sewing_type_id || soi.fabricSewingTypeId || (soi.fabric_sewing_type && soi.fabric_sewing_type.id) || null) : null;
                        const name = getFabricTypeName(fabricId);
                        if (!fabricId || name === '-') {
                          return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">-</span>;
                        }
                        return <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-100">{name}</span>;
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(() => {
                        const measurements = getMeasurements(task);
                        if (!measurements) {
                          return <span className="text-xs text-gray-500">{t('common.missing')}</span>;
                        }
                        const { length, chest, waist, hips } = measurements;
                        return (
                          <div className="text-xs text-gray-700">
                            <div className="font-medium">{t('measurements.length')}:{length || '-'} | {t('measurements.chest')}:{chest || '-'}</div>
                            <div>{t('measurements.waist')}:{waist || '-'} | {t('measurements.hips')}:{hips || '-'}</div>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{getWorkerName(task.worker_id)}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap space-y-1">
                      <div>
                        <span className={`px-3 py-1.5 text-xs font-semibold rounded-full ${statusColors[task.status]}`}>
                          {task.status}
                        </span>
                      </div>
                      {(() => {
                        const soi = task.sales_order_item || task.salesOrderItem || null;
                        const soiId = soi ? soi.id : null;
                        const itemReturnStatus = returnStatus[soiId];
                        if (itemReturnStatus && itemReturnStatus.is_fully_returned) {
                          return (
                            <div>
                              <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-700 border border-red-200">
                                {t('lab.notReturnable')}
                              </span>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {task.created_at ? new Date(task.created_at).toLocaleString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {task.started_at ? new Date(task.started_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setShowTaskDetailsModal(task)}
                          className="text-gray-600 hover:text-gray-900 hover:bg-gray-100 p-2 rounded-lg transition-colors"
                          title="View Details"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                        {(() => {
                          const soi = task.sales_order_item || task.salesOrderItem || null;
                          const soiId = soi ? soi.id : null;
                          const itemReturnStatus = returnStatus[soiId];
                          const isFullyReturned = itemReturnStatus && itemReturnStatus.is_fully_returned;

                          return (
                            <Link
                              to={isFullyReturned ? '#' : `/lab/${task.id}`}
                              onClick={isFullyReturned ? (e) => {
                                e.preventDefault();
                                showWarning(t('lab.notReturnableWarning'));
                              } : null}
                              className={`${isFullyReturned ? 'text-gray-400 cursor-not-allowed bg-gray-100' : 'text-yellow-600 hover:text-yellow-900 hover:bg-yellow-100'} p-2 rounded-lg transition-colors`}
                              title={isFullyReturned ? t('lab.notReturnableWarning') : 'Edit'}
                            >
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </Link>
                          );
                        })()}
                        <button
                          onClick={() => handlePrint(task)}
                          className="text-gray-600 hover:text-gray-900 hover:bg-gray-100 p-2 rounded-lg transition-colors"
                          title="Print"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 9V2h12v7M6 18H4a2 2 0 01-2-2V9h20v7a2 2 0 01-2 2h-2M16 18a4 4 0 01-8 0" />
                          </svg>
                        </button>
                        {task.status === 'Assigned' && (
                          <button
                            onClick={() => handleStartWork(task.id)}
                            className="text-yellow-600 hover:text-yellow-900 hover:bg-yellow-100 p-2 rounded-lg transition-colors"
                            title="Start Work"
                          >
                            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </button>
                        )}
                        {task.status === 'In Progress' && (
                          <button
                            onClick={() => handleCompleteWork(task.id)}
                            className="text-gray-700 hover:text-gray-900 hover:bg-gray-100 p-2 rounded-lg transition-colors"
                            title="Complete"
                          >
                            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                            </svg>
                          </button>
                        )}
                        {(() => {
                          const soi = task.sales_order_item || task.salesOrderItem || null;
                          const soiId = soi ? soi.id : null;
                          const itemReturnStatus = returnStatus[soiId];
                          const isFullyReturned = itemReturnStatus && itemReturnStatus.is_fully_returned;

                          return (
                            <button
                              onClick={() => {
                                if (isFullyReturned) {
                                  showWarning(t('lab.notReturnableWarning'));
                                } else {
                                  handleDelete(task.id);
                                }
                              }}
                              disabled={isFullyReturned}
                              className={`${isFullyReturned ? 'text-gray-400 cursor-not-allowed bg-gray-100' : 'text-red-600 hover:text-red-900 hover:bg-red-100'} p-2 rounded-lg transition-colors`}
                              title={isFullyReturned ? t('lab.notReturnableWarning') : 'Delete'}
                            >
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          );
                        })()}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showTaskDetailsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full">
            <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white p-6 flex justify-between items-center rounded-t-xl">
              <h3 className="text-xl font-bold">{t('lab.taskDetails')} - {showTaskDetailsModal.lab_number}</h3>
              <button onClick={() => setShowTaskDetailsModal(null)} className="text-white hover:text-yellow-400 transition-colors text-2xl">✕</button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">{t('common.status')}</p>
                  <p className="font-semibold">{showTaskDetailsModal.status}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t('lab.assignedWorker')}</p>
                  <p className="font-semibold">{getWorkerName(showTaskDetailsModal.worker_id)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t('lab.started')}</p>
                  <p className="font-semibold">
                    {showTaskDetailsModal.started_at ? new Date(showTaskDetailsModal.started_at).toLocaleString() : t('common.notStarted')}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t('lab.completed')}</p>
                  <p className="font-semibold">
                    {showTaskDetailsModal.completed_at ? new Date(showTaskDetailsModal.completed_at).toLocaleString() : t('common.notCompleted')}
                  </p>
                </div>
              </div>
              {(() => {
                const soi = showTaskDetailsModal.sales_order_item || showTaskDetailsModal.salesOrderItem || null;
                const m = soi && (soi.measurement || null);
                if (!m) return null;
                return (
                  <div className="mt-4">
                    <p className="text-sm text-gray-600">{t('measurements.bodyMeasurements')}</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
                      <div className="bg-gray-50 rounded-lg p-3"><div className="text-xs text-gray-500">{t('measurements.length')}</div><div className="font-semibold">{m.length ?? '-'}</div></div>
                      <div className="bg-gray-50 rounded-lg p-3"><div className="text-xs text-gray-500">{t('measurements.chest')}</div><div className="font-semibold">{m.chest ?? '-'}</div></div>
                      <div className="bg-gray-50 rounded-lg p-3"><div className="text-xs text-gray-500">{t('measurements.waist')}</div><div className="font-semibold">{m.waist ?? '-'}</div></div>
                      <div className="bg-gray-50 rounded-lg p-3"><div className="text-xs text-gray-500">{t('measurements.hips')}</div><div className="font-semibold">{m.hips ?? '-'}</div></div>
                      <div className="bg-gray-50 rounded-lg p-3"><div className="text-xs text-gray-500">{t('measurements.shoulder')}</div><div className="font-semibold">{m.shoulder ?? '-'}</div></div>
                      <div className="bg-gray-50 rounded-lg p-3"><div className="text-xs text-gray-500">{t('measurements.sleeve')}</div><div className="font-semibold">{m.sleeve ?? '-'}</div></div>
                      <div className="bg-gray-50 rounded-lg p-3"><div className="text-xs text-gray-500">{t('measurements.neck')}</div><div className="font-semibold">{m.neck ?? '-'}</div></div>
                    </div>
                  </div>
                );
              })()}
              <div className="mt-4">
                <p className="text-sm text-gray-600">Fabric Type</p>
                <p className="font-semibold">{
                  (() => {
                    const soi = showTaskDetailsModal.sales_order_item || showTaskDetailsModal.salesOrderItem || null;
                    const fabricId = soi ? (soi.fabric_sewing_type_id || soi.fabricSewingTypeId || (soi.fabric_sewing_type && soi.fabric_sewing_type.id) || null) : null;
                    const name = getFabricTypeName(fabricId);
                    if (!fabricId || name === '-') {
                      return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">-</span>;
                    }
                    return <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-100">{name}</span>;
                  })()
                }</p>
              </div>
              {showTaskDetailsModal.notes && (
                <div>
                  <p className="text-sm text-gray-600">{t('common.notes')}</p>
                  <p className="font-semibold">{showTaskDetailsModal.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <LabBulkAssignmentModal
        isOpen={showBulkAssignModal}
        onClose={() => setShowBulkAssignModal(false)}
        onSuccess={loadData}
      />
    </div>
  );
};

export default LabList;
